import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { label: "首页", to: "/" },
  { label: "分类", to: "/category" },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setKeyword("");
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || menuOpen
          ? "bg-background-950/95 backdrop-blur border-b border-background-800"
          : "bg-gradient-to-b from-background-950/90 to-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 shrink-0 group">
            <span className="w-9 h-9 rounded-md bg-primary-600 flex items-center justify-center text-background-50 text-lg">
              <i className="ri-movie-line" />
            </span>
            <span className="font-heading font-bold text-foreground-50 text-base md:text-lg tracking-wide whitespace-nowrap">
              饥渴的岳<span className="text-accent-500">中文字幕</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-md text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                  isActive(link.to)
                    ? "text-accent-500 bg-primary-950/60"
                    : "text-foreground-200 hover:text-accent-400 hover:bg-background-800/70"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={submitSearch}
              className="hidden lg:flex items-center gap-2 bg-background-900 border border-background-700 rounded-full pl-3 pr-1 h-10 w-64 focus-within:border-accent-600 transition-colors"
            >
              <i className="ri-search-line text-foreground-400 text-base" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片、导演、类型"
                className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-100 placeholder:text-foreground-500 font-label"
              />
              <button
                type="submit"
                className="w-8 h-8 rounded-full bg-primary-600 hover:bg-primary-500 text-background-50 flex items-center justify-center transition-colors cursor-pointer shrink-0"
                aria-label="搜索"
              >
                <i className="ri-arrow-right-line text-sm" />
              </button>
            </form>

            <Link
              to="/category"
              className="hidden sm:flex items-center gap-1.5 px-4 h-10 rounded-full border border-accent-700 text-accent-400 hover:bg-accent-500 hover:text-background-950 transition-colors text-sm font-label whitespace-nowrap cursor-pointer"
            >
              <i className="ri-list-check-2" />
              全部影片
            </Link>

            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-100 hover:bg-background-800 text-xl cursor-pointer"
              aria-label="菜单"
            >
              <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-800 bg-background-950 px-4 pb-5 pt-3">
          <form
            onSubmit={submitSearch}
            className="flex items-center gap-2 bg-background-900 border border-background-700 rounded-full pl-3 pr-1 h-11 w-full mb-3 focus-within:border-accent-600"
          >
            <i className="ri-search-line text-foreground-400 text-base" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、导演、类型"
              className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-100 placeholder:text-foreground-500 font-label"
            />
            <button
              type="submit"
              className="w-9 h-9 rounded-full bg-primary-600 text-background-50 flex items-center justify-center shrink-0 cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-arrow-right-line text-sm" />
            </button>
          </form>
          <div className="flex flex-col">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-3 rounded-md text-sm font-label cursor-pointer ${
                  isActive(link.to)
                    ? "text-accent-500 bg-primary-950/60"
                    : "text-foreground-200 hover:bg-background-800/70"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}